import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils/cn';

export type Aal2Method = 'totp' | 'lookup_secret';
export type Aal2FieldName = 'totp_code' | 'lookup_secret';

export interface Aal2CodeFormProps {
  /** What kind of code is this — drives styling (centered monospace for TOTP). */
  method: Aal2Method;
  /** The Kratos node name that carries the code in the POST body. */
  fieldName: Aal2FieldName;
  /** Heading + sub-copy shown above the input. */
  label: string;
  description: string;
  /** Placeholder text for the input. */
  placeholder?: string;
  /** Native input mode hint — `numeric` for TOTP, `text` for backup. */
  inputMode?: 'numeric' | 'text';
  /** Max length and HTML5 pattern (TOTP = 6 digits). */
  maxLength?: number;
  pattern?: string;
  /** Inline error to show under the input (typically from Kratos node messages). */
  error?: string;
  disabled?: boolean;
  /** Parent submits the code via fetch / login mutation. */
  onSubmit: (method: Aal2Method, fieldName: Aal2FieldName, value: string) => void;
}

/**
 * Self-contained form for AAL2 step-up methods (TOTP code, backup code).
 *
 * Stays UI-only — the parent owns the API call and the loading state via
 * `disabled` + `error` props. The look matches `TotpSetupForm` for visual
 * coherence across the dashboard auth flow.
 */
export function Aal2CodeForm({
  method,
  fieldName,
  label,
  description,
  placeholder,
  inputMode = 'numeric',
  maxLength,
  pattern,
  error,
  disabled,
  onSubmit,
}: Aal2CodeFormProps) {
  const { t } = useTranslation();
  const [value, setValue] = React.useState('');

  const isTotp = method === 'totp';
  // For TOTP we strip non-digits + cap at maxLength so the input behaves like
  // a clean OTP field even when the user pastes a code with spaces.
  const handleChange = (raw: string) => {
    if (isTotp) {
      const cleaned = raw.replace(/\D/g, '');
      setValue(maxLength ? cleaned.slice(0, maxLength) : cleaned);
    } else {
      setValue(raw.trim());
    }
  };

  const minLength = isTotp ? 6 : 1;
  const ready = value.length >= minLength && !disabled;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    onSubmit(method, fieldName, value);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1 text-center">
        <h3 className="text-sm font-medium text-foreground">{label}</h3>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>

      <div className="space-y-2">
        <input
          name={fieldName}
          type="text"
          inputMode={inputMode}
          autoComplete={isTotp ? 'one-time-code' : 'off'}
          autoFocus
          maxLength={maxLength}
          pattern={pattern}
          placeholder={placeholder}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          disabled={disabled}
          required
          aria-invalid={error ? 'true' : undefined}
          className={cn(
            'flex h-12 w-full rounded-md border border-input bg-background px-3 py-2',
            isTotp
              ? 'text-center font-mono text-lg tracking-[0.5em]'
              : 'font-mono text-base',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-destructive focus-visible:ring-destructive'
          )}
        />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}

      <Button
        type="submit"
        className="w-full h-11"
        disabled={!ready}
        aria-disabled={!ready}
      >
        {disabled ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('auth.settingsSubmitting')}
          </>
        ) : (
          <>
            <ShieldCheck className="mr-2 h-4 w-4" />
            {t('auth.mfaTotpVerify')}
          </>
        )}
      </Button>
    </form>
  );
}
